import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

/**
 * Mutation hook to start an automation for a single specific contact.
 *
 * This mutation creates an automation instance for one contact and optionally
 * starts them progressing through the steps immediately. It's distinct from
 * useStartAutomationMutation which activates the automation for all eligible contacts.
 *
 * Use cases:
 * - Testing an automation flow with a specific contact before full rollout
 * - Manually enrolling a contact who doesn't match trigger criteria
 * - Previewing how a specific contact would progress through the automation
 * - QA/debugging automation behavior with known contact data
 *
 * Start parameter:
 * - start=true: Contact immediately begins progressing through steps
 * - start=false: Automation instance is created but contact doesn't start yet
 *   (useful for preview/testing without actually executing steps)
 *
 * Returns both:
 * - automation: The created automation instance for this contact
 * - template: The template reference (useful for navigation/display)
 *
 * This is different from useStartAutomationMutation which starts the automation
 * for all eligible contacts in bulk.
 *
 * @param options - Mutation configuration
 * @param options.impersonationContext - Optional impersonation context for admin operations
 * @param options.onSuccess - Callback with created automation and template
 * @param options.onError - Callback invoked on error
 * @returns React Query mutation with mutateAsync function
 *
 * @example
 * ```typescript
 * const startForContactMutation = useStartAutomationForContactMutation({
 *   onSuccess: ({ automation, template }) => {
 *     invalidateAutomationTemplateContactStepsQueries(template.id, contactId);
 *     showToast(`Started automation for ${contact.name}`);
 *     navigate(`/automation/${automation.id}/contact/${contactId}`);
 *   },
 *   onError: (error) => {
 *     showErrorToast(error.systemMessage || 'Failed to start automation');
 *   }
 * });
 *
 * // In a test/preview button:
 * <Button
 *   onClick={() => startForContactMutation.mutateAsync({
 *     template: automationTemplate,
 *     contactId: selectedContact.id,
 *     start: true
 *   })}
 *   loading={startForContactMutation.isPending}
 * >
 *   Test with {selectedContact.name}
 * </Button>
 * ```
 *
 * @example
 * ```typescript
 * // Preview without executing steps:
 * const handlePreview = async () => {
 *   const { automation } = await startForContactMutation.mutateAsync({
 *     template: automationTemplate,
 *     contactId: testContactId,
 *     start: false // Don't actually execute, just create instance
 *   });
 *
 *   // Now you can inspect what would happen without sending emails, etc.
 *   showPreviewDialog(automation);
 * };
 * ```
 */
export function useStartAutomationForContactMutation({
	impersonationContext,
	onSuccess,
	onError,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onSuccess?: (data: { automation: Api.IAutomation; template: Api.IAutomationTemplateReference }) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
}) {
	const userSession = useUserSession();

	return useMutation({
		mutationKey: ['startAutomationForContact'],
		onSuccess: data => {
			if (onSuccess) {
				onSuccess(data);
			}
		},
		onError,
		mutationFn: async ({
			template,
			contactId,
			start,
		}: {
			template: Api.IAutomationTemplateReference;
			contactId: string;
			start: boolean;
		}) => {
			const automation = await userSession.webServiceHelper.callAsync<Api.IAutomation>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `Automation/${template.id}/Contact/${contactId}`,
					queryParams: { start },
				}),
				'POST',
				null
			);
			return { automation, template };
		},
	});
}
